import { useCallback, useEffect, useRef } from 'react';

import { ImpactStyle, triggerHaptic } from '@/utils/haptics';

type LongPressOptions = {
  delay?: number;
  tolerance?: number;
  haptic?: boolean;
};

type LongPressHandlers = {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
  onPointerLeave: (event: React.PointerEvent) => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onClickCapture: (event: React.MouseEvent) => void;
};

export const useLongPress = (
  onLongPress: () => void,
  { delay = 500, tolerance = 8, haptic = true }: LongPressOptions = {},
): LongPressHandlers => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
  }, []);

  useEffect(() => () => clear(), [clear]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      firedRef.current = false;
      startPosRef.current = { x: event.clientX, y: event.clientY };
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        timerRef.current = null;
        if (haptic) triggerHaptic(ImpactStyle.Medium);
        onLongPress();
      }, delay);
    },
    [delay, haptic, onLongPress],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!startPosRef.current || !timerRef.current) return;
      const dx = event.clientX - startPosRef.current.x;
      const dy = event.clientY - startPosRef.current.y;
      if (Math.hypot(dx, dy) > tolerance) clear();
    },
    [clear, tolerance],
  );

  const onPointerUp = useCallback(() => clear(), [clear]);
  const onPointerCancel = useCallback(() => clear(), [clear]);
  const onPointerLeave = useCallback(() => clear(), [clear]);

  const onContextMenu = useCallback((event: React.MouseEvent) => {
    if (firedRef.current) event.preventDefault();
  }, []);

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (firedRef.current) {
      event.preventDefault();
      event.stopPropagation();
      firedRef.current = false;
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    onContextMenu,
    onClickCapture,
  };
};
